import { useState, useEffect } from 'react';
import { adminAPI, documentsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { FileText, CheckCircle, XCircle, Eye, Search, Filter } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function AdminBulkDocs() {
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.pendingDocuments({ page, limit: 50 });
      setDocuments(res.data.documents);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); }, [page]);

  const toggleSelect = (docId) => {
    setSelected(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleAll = () => {
    if (selected.length === documents.length) {
      setSelected([]);
    } else {
      setSelected(documents.map(d => d.id));
    }
  };

  const handleBulkApprove = async () => {
    if (selected.length === 0) return;
    setProcessing(true);
    try {
      const res = await adminAPI.bulkApprove(selected);
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => {
          toast.error(`${err.fileName || err.id}: ${err.reason}`);
        });
      }
      setSelected([]);
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na aprovacao em lote');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selected.length === 0) return;
    setProcessing(true);
    try {
      const res = await adminAPI.bulkReject(selected, rejectReason || 'Rejeitado em lote');
      toast.success(res.data.message);
      setSelected([]);
      setRejectReason('');
      setBulkAction(null);
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na rejeicao em lote');
    } finally {
      setProcessing(false);
    }
  };

  const getOcrBadge = (doc) => {
    if (doc.ocrValidation === 'match') return <span className="badge-success text-[10px]">OCR: OK</span>;
    if (doc.ocrValidation === 'mismatch') return <span className="badge-danger text-[10px]">OCR: Divergente</span>;
    return <span className="badge-neutral text-[10px]">OCR: Indefinido</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-bulk-docs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Documentos Pendentes</h1>
          <p className="text-sm text-white/50">{total} documento(s) aguardando revisao</p>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[rgba(0,255,163,0.06)] border border-[rgba(0,255,163,0.2)]">
          <span className="text-sm font-medium text-[var(--rn-green)]">
            {selected.length} selecionado(s)
          </span>
          <div className="flex-1" />
          <button
            onClick={handleBulkApprove}
            disabled={processing}
            className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
            data-testid="bulk-approve-button"
          >
            {processing ? <span className="rn-spinner" style={{width:14,height:14}} /> : <><CheckCircle size={14} /> Aprovar Selecionados</>}
          </button>
          <button
            onClick={() => setBulkAction('reject')}
            disabled={processing}
            className="btn-destructive text-sm py-2 px-4 flex items-center gap-2"
            data-testid="bulk-reject-button"
          >
            <XCircle size={14} /> Rejeitar Selecionados
          </button>
          <button
            onClick={() => setSelected([])}
            className="btn-ghost text-sm py-2 px-3"
          >
            Limpar
          </button>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={bulkAction === 'reject'} onOpenChange={() => setBulkAction(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Rejeitar {selected.length} documento(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-white/70 mb-1.5">Motivo da rejeicao</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Informe o motivo..."
                rows={3}
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)]"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setBulkAction(null)} className="btn-ghost flex-1">Cancelar</button>
              <button onClick={handleBulkReject} disabled={processing} className="btn-destructive flex-1">
                {processing ? <span className="rn-spinner mx-auto" style={{width:14,height:14}} /> : 'Confirmar Rejeicao'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Table */}
      <div className="rn-card overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-8"><div className="rn-spinner" style={{width:24,height:24}} /></div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12" data-testid="empty-state">
            <CheckCircle size={48} className="mx-auto text-[var(--rn-green)] opacity-30 mb-3" />
            <h3 className="font-display text-lg font-semibold mb-1">Nenhum documento pendente</h3>
            <p className="text-sm text-white/40">Todos os documentos foram revisados.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <Checkbox
                checked={selected.length === documents.length && documents.length > 0}
                onCheckedChange={toggleAll}
                className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]"
              />
              <span className="text-sm text-white/50">Selecionar todos</span>
            </div>

            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                    selected.includes(doc.id)
                      ? 'bg-[rgba(0,255,163,0.04)] border-[rgba(0,255,163,0.2)]'
                      : 'bg-white/[0.02] border-white/10 hover:border-white/15'
                  }`}
                >
                  <Checkbox
                    checked={selected.includes(doc.id)}
                    onCheckedChange={() => toggleSelect(doc.id)}
                    className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]"
                  />

                  <FileText size={20} className="text-white/40 flex-shrink-0" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">{doc.fileName}</p>
                      {getOcrBadge(doc)}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-white/50">
                      <span>{doc.user?.name || 'N/A'}</span>
                      <span>{doc.case?.docType}: {doc.case?.docFormatted}</span>
                      <span>{new Date(doc.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {doc.ocrValidation === 'mismatch' && (
                      <p className="text-xs text-red-400 mt-1">
                        OCR divergente: documento identificou {doc.extractedDocDigits || 'N/A'} mas caso tem {doc.case?.docDigits || 'N/A'}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <a href={documentsAPI.getFileUrl(doc.id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/5">
                      <Eye size={14} className="text-white/60" />
                    </a>
                    <button
                      onClick={async () => {
                        try {
                          await documentsAPI.approve(doc.id);
                          toast.success('Documento aprovado!');
                          fetchDocs();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Erro');
                        }
                      }}
                      className="p-2 rounded-lg hover:bg-[rgba(0,255,163,0.1)]"
                      title="Aprovar"
                    >
                      <CheckCircle size={14} className="text-[var(--rn-green)]" />
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await documentsAPI.reject(doc.id, 'Rejeitado pelo administrador');
                          toast.success('Documento rejeitado');
                          fetchDocs();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Erro');
                        }
                      }}
                      className="p-2 rounded-lg hover:bg-[rgba(239,68,68,0.1)]"
                      title="Rejeitar"
                    >
                      <XCircle size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
                <span className="text-xs text-white/40">Pagina {page} de {pages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Anterior</button>
                  <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Proximo</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
